import { ImagePaths } from '../../lib/imageUrls.js';

export const smbCybersecurityEssentials = {

    id: 14,
    slug: 'smb-cybersecurity-essentials',
    ogImage: '/og/courses/smb-cybersecurity-essentials.jpg',
    courseCode: 'SMB-CYBER-ESS-001',
    title: 'SMB Cybersecurity Essentials: Understanding Threats and Basic Defences',
    subtitle: 'Practical cybersecurity for small and medium businesses',
    valueProposition: 'Protect your SMB from common cyber threats with practical, low-cost security solutions that you can implement immediately—no IT expertise required.',
    duration: '8 hours',
    students: 'New',
    level: 'Foundational',
    image: ImagePaths.COURSE_1,
    shortDescription: 'SMB-CYBER-ESS-001 — Practical cybersecurity defences covering phishing, ransomware, passwords, and basic controls for Australian SMB owners and staff.',
    price: {
      aud: 999,
      currency: 'AUD',
      earlyBird: 749
    },
    stripeConfig: {
    priceId: 'price_1T9bE6Ku1nyc322UltKSZRl5',
    productId: 'prod_U7qvAww5OedPDr'
  },
    
    snapshot: {
      whoItsFor: [
        'SMB owners and managers',
        'Employees with limited IT knowledge',
        'Business operators without dedicated IT staff',
        'Anyone responsible for business security decisions'
      ],
      accreditation: 'Aligned to Essential Eight and ACSC Small Business Cyber Security Guide'
    },
    
    logistics: {
      classSize: 'Maximum 25 participants for interactive discussion',
      requirements: [
        'No technical background required',
        'Computer or tablet with internet access',
        'Reliable internet connection (minimum 5 Mbps)',
        'Willingness to implement practical security measures'
      ]
    },
    
    overview: 'This practical 8-hour course equips SMB owners, managers, and employees with essential knowledge to protect their business from the most common cyber threats. You\'ll learn about real-world attack vectors targeting Australian SMBs—including phishing, ransomware, weak passwords, and social engineering—and discover low-cost, readily implementable defences. With no technical jargon and a focus on practical action, you\'ll leave with a clear security checklist tailored to small business realities and budgets. Perfect for businesses without dedicated IT staff who need to build baseline cyber resilience.',
    
    outcomes: [
      'Identify common cyber threats targeting Australian SMBs including phishing, ransomware, and malware',
      'Implement fundamental password security practices and multi-factor authentication',
      'Recognise suspicious emails, links, and social engineering attempts',
      'Apply basic network security controls including secure Wi-Fi and router configuration',
      'Protect business data with simple backup strategies and file security',
      'Respond appropriately to suspected security incidents',
      'Create a practical security checklist for your business',
      'Educate employees on essential security awareness practices'
    ],
    
    audience: [
      'Small and medium business owners',
      'Business managers and operators',
      'Administrative staff and office managers',
      'Employees with limited technical knowledge',
      'Anyone responsible for business security decisions',
      'Sole traders and contractors'
    ],
    
    prerequisites: [
      'No cybersecurity or technical experience required',
      'Basic computer and internet skills',
      'Authority to implement changes in your organisation (for owners/managers)'
    ],
    
    modules: [
      {
        title: 'Understanding the SMB Threat Landscape',
        duration: '2 hours',
        topics: [
          'Why cybercriminals target small businesses',
          'Common attack vectors: phishing, ransomware, malware, social engineering',
          'Real Australian SMB breach case studies',
          'The cost of a cyber incident to small businesses',
          'Australian Cyber Security Centre (ACSC) small business guidance',
          'Understanding your security responsibilities'
        ]
      },
      {
        title: 'Password Security and Authentication',
        duration: '1.5 hours',
        topics: [
          'The problem with weak and reused passwords',
          'Creating strong, memorable passwords (passphrases)',
          'Password managers for small businesses',
          'Multi-factor authentication (MFA) explained simply',
          'Implementing MFA on common business tools',
          'Secure password sharing in teams'
        ]
      },
      {
        title: 'Recognising and Defending Against Phishing',
        duration: '1.5 hours',
        topics: [
          'What is phishing and how does it work?',
          'Spotting suspicious emails, SMS, and phone calls',
          'Common phishing tactics targeting Australian businesses',
          'Safe link and attachment practices',
          'What to do when you receive a suspicious message',
          'Reporting phishing to authorities and your IT provider'
        ]
      },
      {
        title: 'Ransomware and Malware Protection',
        duration: '1.5 hours',
        topics: [
          'Understanding ransomware and its impact on SMBs',
          'How ransomware spreads and infects systems',
          'Basic endpoint protection (antivirus and anti-malware)',
          'Keeping software and systems up to date',
          'Safe browsing and download practices',
          'Backup strategies to protect against ransomware'
        ]
      },
      {
        title: 'Network and Device Security',
        duration: '1 hour',
        topics: [
          'Securing your business Wi-Fi network',
          'Router security basics and default password changes',
          'Separating guest and business networks',
          'Basic firewall concepts for non-technical users',
          'Mobile device security for BYOD environments',
          'Remote work security considerations'
        ]
      },
      {
        title: 'Building Your SMB Security Action Plan',
        duration: '0.5 hours',
        topics: [
          'Creating your security implementation checklist',
          'Prioritising controls based on your business needs',
          'Low-cost and free security tools for SMBs',
          'Training your team on security basics',
          'When to seek professional IT security help',
          'Ongoing security maintenance and vigilance'
        ]
      }
    ],
    
    format: {
      type: 'Live webinar or self-paced online modules',
      includes: [
        '8 hours of practical instruction',
        'SMB security checklist and implementation guide',
        'Phishing identification exercises',
        'Password and MFA setup guides',
        'Real-world case studies from Australian SMBs',
        'Access to recorded sessions for 6 months',
        'Certificate of completion',
        'Email support for 30 days post-course'
      ]
    },
    
    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Two-day live webinar (4 hours per day) or self-paced access available immediately'
    },
    
    faqs: [
      {
        question: 'Do I need technical knowledge to take this course?',
        answer: 'No technical knowledge is required. This course is designed specifically for SMB owners, managers, and employees without IT backgrounds. We explain everything in plain language and focus on practical actions you can take immediately.'
      },
      {
        question: 'How much will it cost to implement the security measures covered?',
        answer: 'Most controls covered are free or very low-cost (under $500 total for a typical small business). We focus on built-in security features, free tools, and affordable solutions suitable for SMB budgets. Many defences require only time and awareness, not financial investment.'
      },
      {
        question: 'Is this course relevant for businesses without IT staff?',
        answer: 'Absolutely—this course is designed specifically for SMBs without dedicated IT teams. You\'ll learn what you can do yourself and when it\'s appropriate to engage external IT support. All recommendations are practical for businesses managing their own technology.'
      },
      {
        question: 'Will this course help us comply with Australian regulations?',
        answer: 'While not a compliance course, the security practices covered align with Australian Privacy Principles (APPs), Essential Eight guidance, and ACSC recommendations for small businesses. You\'ll build foundational security that supports compliance with data protection obligations.'
      },
      {
        question: 'Can we enrol multiple employees from our business?',
        answer: 'Yes, and we encourage it! For teams of 5 or more, contact us for a private quote. Training your entire team creates a security-aware culture, which is one of the most effective defences for SMBs.'
      },
      {
        question: 'What if we\'ve already experienced a cyber incident?',
        answer: 'This course covers immediate response steps and prevention strategies. If you\'re currently dealing with an active incident, contact the ACSC via cyber.gov.au or call 1300 CYBER1 for emergency assistance. This course will help you prevent future incidents and improve your security posture.'
      },
      {
        question: 'How is this different from general IT training?',
        answer: 'This course focuses specifically on cybersecurity threats and defences relevant to Australian SMBs. We don\'t cover general IT skills—only practical security measures to protect against the specific attacks targeting small businesses. It\'s targeted, actionable, and business-focused.'
      },
      {
        question: 'Will we receive ongoing support after the course?',
        answer: 'Yes, you\'ll have email support for 30 days after course completion to ask questions as you implement security measures in your business. You\'ll also receive access to updated resources and security alerts relevant to Australian SMBs.'
      }
    ]
};
